import { useEffect, useState } from "react";

/**
 * Subscribe to a CSS media query so a component only renders the branch
 * that will actually be visible. Prevents the classic `hidden lg:block` /
 * `lg:hidden` pitfall where both branches mount their subtree (and fire
 * their `useEffect` data fetches) even though only one is visible.
 *
 * SSR-safe: falls back to false when `window` is unavailable.
 */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined") return false;
    try { return window.matchMedia(query).matches; } catch { return false; }
  });

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mql = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    // Modern browsers use addEventListener; Safari <14 used addListener.
    if (mql.addEventListener) {
      mql.addEventListener("change", handler);
      setMatches(mql.matches);
      return () => mql.removeEventListener("change", handler);
    }
    mql.addListener(handler);
    setMatches(mql.matches);
    return () => mql.removeListener(handler);
  }, [query]);

  return matches;
}

/** Convenience: matches Tailwind's `lg` breakpoint. */
export const useIsDesktop = () => useMediaQuery("(min-width: 1024px)");
